import { useEffect, useMemo, useState } from 'react';
import RefereePanel from './RefereePanel';
import { API_BASE } from './config';
import '../css/CourtManager.css';

const COURT_NUMBERS = Array.from({ length: 18 }, (_, index) => index + 1);

const STATUS_LABELS = {
  OPEN: 'ว่าง',
  CHECK: 'กำลังใช้งาน',
  PLAY: 'กำลังแข่งขัน',
  CLOSED: 'ปิด',
  NOT_CREATED: 'ยังไม่สร้าง'
};

const normalizeStatus = status =>
  String(status || 'OPEN').trim().toUpperCase();

const requestJson = async (url, options) => {
  const response = await fetch(`${API_BASE}${url}`, options);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || data.error || `HTTP ${response.status}`);
  }

  return data;
};

function CourtManager({ groupId, courts = [] }) {
  const [courtList, setCourtList] = useState(courts);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [currentMatch, setCurrentMatch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setCourtList(Array.isArray(courts) ? courts : []);
  }, [courts]);

  useEffect(() => {
    setSelectedCourt(null);
    setCurrentMatch([]);
    setError('');
  }, [groupId]);

  const refreshCourts = async () => {
    if (!groupId) return [];

    const data = await requestJson(`/api/courts/${groupId}`);
    const nextCourts = Array.isArray(data) ? data : [];
    setCourtList(nextCourts);
    return nextCourts;
  };

  useEffect(() => {
    if (!groupId) return undefined;

    const handleCourtsChanged = async event => {
      if (Number(event.detail?.groupId) !== Number(groupId)) return;

      try {
        const data = await requestJson(`/api/courts/${groupId}`);
        setCourtList(Array.isArray(data) ? data : []);
        setError('');
      } catch (requestError) {
        setError(`อัปเดตรายการ Court ไม่สำเร็จ: ${requestError.message}`);
      }
    };

    window.addEventListener('courts-changed', handleCourtsChanged);

    return () => {
      window.removeEventListener('courts-changed', handleCourtsChanged);
    };
  }, [groupId]);

  const courtSlots = useMemo(() => {
    const courtByNumber = new Map(
      courtList.map(court => [Number(court.court_no), court])
    );

    return COURT_NUMBERS.map(courtNo => {
      const savedCourt = courtByNumber.get(courtNo);

      return savedCourt
        ? { ...savedCourt, court_no: courtNo }
        : {
            id: null,
            group_id: Number(groupId),
            court_no: courtNo,
            status: 'NOT_CREATED'
          };
    });
  }, [courtList, groupId]);

  const selectedCourtData = useMemo(
    () => courtSlots.find(court => court.court_no === selectedCourt) || null,
    [courtSlots, selectedCourt]
  );

  const openCourtCount = courtSlots.filter(
    court => normalizeStatus(court.status) === 'OPEN'
  ).length;

  const activeCourtCount = courtSlots.filter(court =>
    ['CHECK', 'PLAY'].includes(normalizeStatus(court.status))
  ).length;

  const selectCourt = async courtNo => {
    const court = courtSlots.find(item => item.court_no === courtNo);

    setSelectedCourt(courtNo);
    setCurrentMatch([]);
    setError('');

    if (!court?.id) return;

    try {
      setLoading(true);

      const data = await requestJson(
        `/api/courts/${groupId}/${courtNo}/match`
      );
      const matchPlayers = Array.isArray(data)
        ? data
        : Array.isArray(data.players)
          ? data.players
          : [];

      setCurrentMatch(matchPlayers);
    } catch (requestError) {
      setError(`โหลด Court ${courtNo} ไม่สำเร็จ: ${requestError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const checkInCourt = async courtNo => {
    try {
      setBusyAction('checkin');
      setError('');

      await requestJson('/api/courts/checkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupId: Number(groupId),
          courtIds: [Number(courtNo)],
          action: 'CHECK'
        })
      });

      await refreshCourts();
      await selectCourt(courtNo);
    } catch (requestError) {
      setError(`Check-in ไม่สำเร็จ: ${requestError.message}`);
    } finally {
      setBusyAction('');
    }
  };

  const checkOutCourt = async courtNo => {
    try {
      setBusyAction('checkout');
      setError('');

      await requestJson('/api/courts/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupId: Number(groupId),
          courtNo: Number(courtNo)
        })
      });

      await refreshCourts();
      setCurrentMatch([]);
    } catch (requestError) {
      setError(`Check-out ไม่สำเร็จ: ${requestError.message}`);
    } finally {
      setBusyAction('');
    }
  };

  const deleteCourt = async court => {
    if (!court?.id) return;
    if (!window.confirm(`ต้องการลบ Court ${court.court_no} ใช่หรือไม่?`)) {
      return;
    }

    try {
      setBusyAction('delete');
      setError('');

      await requestJson(`/api/courts/${court.id}`, {
        method: 'DELETE'
      });

      await refreshCourts();
      setCurrentMatch([]);
    } catch (requestError) {
      setError(`ลบ Court ไม่สำเร็จ: ${requestError.message}`);
    } finally {
      setBusyAction('');
    }
  };

  const matchId = currentMatch[0]?.matchId ?? currentMatch[0]?.match_id;

  return (
    <section className="cm-container">
      <div className="cm-card cm-selector-card">
        <div className="cm-header">
          <div>
            <span className="cm-eyebrow">COURT CONTROL</span>
            <h2>🏟 เลือกหมายเลข Court</h2>
            <p>กดเลือก Court 1–18 เพื่อดูสถานะและการแข่งขัน</p>
          </div>

          <div className="cm-summary">
            <span className="cm-summary-chip open">
              <i /> ว่าง {openCourtCount}
            </span>
            <span className="cm-summary-chip active">
              <i /> ใช้งาน {activeCourtCount}
            </span>
            <span className="cm-summary-chip total">ทั้งหมด 18</span>
          </div>
        </div>

        <div className="cm-court-grid" role="list" aria-label="หมายเลข Court 1 ถึง 18">
          {courtSlots.map(court => {
            const status = normalizeStatus(court.status);
            const selected = selectedCourt === court.court_no;

            return (
              <button
                type="button"
                key={court.court_no}
                className={`cm-court-selector status-${status.toLowerCase()} ${
                  selected ? 'selected' : ''
                }`}
                onClick={() => selectCourt(court.court_no)}
                aria-current={selected ? 'true' : undefined}
                role="listitem"
              >
                <span className="cm-court-number">{court.court_no}</span>
                <span className="cm-court-label">Court</span>
                <span className="cm-court-status">
                  <i /> {STATUS_LABELS[status] || status}
                </span>
              </button>
            );
          })}
        </div>

        {error && <div className="cm-error">❌ {error}</div>}
      </div>

      {selectedCourtData && (
        <div className="cm-card cm-match-panel">
          <div className="cm-selected-header">
            <div className="cm-selected-title">
              <span className="cm-selected-number">{selectedCourt}</span>
              <div>
                <h3>Court {selectedCourt}</h3>
                <span
                  className={`cm-selected-status status-${normalizeStatus(
                    selectedCourtData.status
                  ).toLowerCase()}`}
                >
                  <i />
                  {STATUS_LABELS[normalizeStatus(selectedCourtData.status)] ||
                    normalizeStatus(selectedCourtData.status)}
                </span>
              </div>
            </div>

            <div className="cm-actions">
              {!selectedCourtData.id ? (
                <span className="cm-missing-note">
                  เลือกเพิ่ม Court {selectedCourt} จากการ์ดด้านบน
                </span>
              ) : normalizeStatus(selectedCourtData.status) === 'OPEN' ? (
                <button
                  type="button"
                  className="cm-btn primary"
                  disabled={Boolean(busyAction)}
                  onClick={() => checkInCourt(selectedCourt)}
                >
                  {busyAction === 'checkin' ? 'กำลัง Check-in...' : '✓ Check-in'}
                </button>
              ) : (
                <button
                  type="button"
                  className="cm-btn danger"
                  disabled={Boolean(busyAction)}
                  onClick={() => checkOutCourt(selectedCourt)}
                >
                  {busyAction === 'checkout'
                    ? 'กำลัง Check-out...'
                    : '↩ Check-out'}
                </button>
              )}

              {selectedCourtData.id &&
                normalizeStatus(selectedCourtData.status) === 'OPEN' && (
                  <button
                    type="button"
                    className="cm-btn delete"
                    disabled={Boolean(busyAction)}
                    onClick={() => deleteCourt(selectedCourtData)}
                  >
                    {busyAction === 'delete' ? 'กำลังลบ...' : '🗑 ลบ'}
                  </button>
                )}

              <button
                type="button"
                className="cm-btn ghost"
                onClick={() => {
                  setSelectedCourt(null);
                  setCurrentMatch([]);
                  setError('');
                }}
              >
                ✕ ปิด
              </button>
            </div>
          </div>

          {loading ? (
            <div className="cm-state-message">กำลังโหลดการแข่งขัน...</div>
          ) : currentMatch.length === 0 ? (
            <div className="cm-state-message empty">
              <span>🏸</span>
              <strong>ยังไม่มีการแข่งขันใน Court นี้</strong>
              <small>เลือกหมายเลขอื่น หรือเริ่ม Match จากหน้า Matchmaking</small>
            </div>
          ) : (
            <>
              <div className="cm-teams">
                {[1, 2].map(teamNumber => (
                  <div
                    key={teamNumber}
                    className={`cm-team-box team-${teamNumber}`}
                  >
                    <h4>Team {teamNumber}</h4>
                    {currentMatch
                      .filter(player => Number(player.team) === teamNumber)
                      .map(player => (
                        <div className="cm-player" key={player.user_id}>
                          <span>{player.name}</span>
                          {player.point !== undefined && (
                            <strong>{player.point}</strong>
                          )}
                        </div>
                      ))}
                  </div>
                ))}
              </div>

              {matchId && (
                <RefereePanel
                  matchId={matchId}
                  onFinish={async () => {
                    setCurrentMatch([]);
                    await refreshCourts();
                  }}
                />
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default CourtManager;
